import { dateFormatter } from "./dateFormatter"

/**
 * Capitalizes the first letter of each word and the dot separated acronyms in a text string.
 *
 * @param {string} text - The text to capitalize.
 * @returns {string} Text with each word capitalized.
 *
 * @description
 * Used for consistently displaying issuer names, labels, or titles.
 */
export const capitalizeEachFirstLetterFormatter = (text) => {
    const cleaned = text.trim().replace(/\s+/g, " ").toLowerCase();
    const baseUpperCase = cleaned.replace(/\b([a-z])/gi, (m) => m.toUpperCase());
    const acronymUpperCase = baseUpperCase.replace(/\b([A-Za-z](?:\.[A-Za-z])+)\./g, (match) =>
        match.toUpperCase()
    );
    return acronymUpperCase

}

/**
 * Formats a list of strings into a human-readable English list.
 *
 * @param {string[]} list - Array of strings to format.
 * @returns {string} Formatted list or "Not provided".
 *
 * @description
 * Uses `Intl.ListFormat` for natural English sentence composition.
 * Returns a fallback message for empty lists.
 */
export const listFormatter = (list) => {
    if (list.length) {
        return (new Intl.ListFormat('en')).format(list)
    }
    return "Not provided"
}

/**
 * Builds a descriptive bond name using issuer, rate, and maturity.
 *
 * @param {{issuerName: string, rate: number, maturity: Date}} bond - Bond object.
 * @returns {string} A formatted bond name (e.g., "Costa Rica 5.25% 2027-01-15").
 *
 * @description
 * Creates a readable bond identifier string following market naming conventions.
 */
export const bondNameFormatter = (bond) => {
    return `${capitalizeEachFirstLetterFormatter(bond.issuerName)} ${bond.rate.toFixed(2)}% ${dateFormatter(bond.maturity, "short")}`
}

/**
 * Joins multiple rating strings into a compact, readable format.
 *
 * @param {...string} ratings - Multiple credit rating strings.
 * @returns {string} A compound rating or "Unavailable".
 *
 * @description
 * Cleans empty entries and joins remaining ratings with slashes ("/").
 */
export const compoundCreditRatingFormatter = (...ratings) => {
    const validRatings = ratings.filter(
        (rating) => typeof rating === 'string' && rating.trim() !== ''
    );

    return validRatings.length > 0 ? validRatings.join('/') : 'Unavailable';
}